import type { ComicItem } from '@/mocks/comicData';
import ComicCard from './ComicCard';

interface RegionSectionProps {
  id: string;
  kicker: string;
  title: string;
  subtitle: string;
  items: ComicItem[];
  icon: string;
  showType: boolean;
  tone: 'primary' | 'accent';
  bg: string;
}

export default function RegionSection({
  id,
  kicker,
  title,
  subtitle,
  items,
  icon,
  showType,
  tone,
  bg,
}: RegionSectionProps) {
  const featured = items[0];
  const rest = items.slice(1);
  const badgeCls = tone === 'primary' ? 'bg-primary-500' : 'bg-accent-500';

  return (
    <section id={id} className={`w-full ${bg} py-14 md:py-20`}>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
                tone === 'primary' ? 'text-primary-600' : 'text-accent-700'
              }`}
            >
              <i className={icon}></i>
              {kicker}
            </span>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">{title}</h2>
            <p className="mt-3 max-w-xl text-sm leading-6 text-foreground-600 md:text-base">{subtitle}</p>
          </div>
          <a
            href="#category"
            className="whitespace-nowrap text-sm font-semibold text-foreground-700 hover:text-primary-600"
          >
            更多同类 <i className="ri-arrow-right-line align-middle"></i>
          </a>
        </div>

        {/* 首部大卡片 + 其余网格，制造不对称布局 */}
        {featured && (
          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-5">
            <article className="group relative flex overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:col-span-2">
              <div className="relative aspect-[4/5] w-1/2 overflow-hidden sm:w-2/5 lg:w-1/2">
                <img
                  src={featured.cover}
                  alt={`${featured.title}漫画封面`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-4 md:p-5">
                <span className={`w-fit whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold text-background-50 ${badgeCls}`}>
                  本周力荐
                </span>
                <h3 className="mt-3 font-heading text-2xl text-foreground-950">{featured.title}</h3>
                <p className="mt-2 line-clamp-4 text-sm leading-6 text-foreground-600">{featured.intro}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {featured.tags.map((t) => (
                    <span key={t} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                  <span className="font-semibold text-primary-600">{featured.latest}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {featured.views}
                  </span>
                </div>
              </div>
            </article>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3">
              {rest.map((item) => (
                <ComicCard key={item.id} item={item} showType={showType} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}